import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
  useConfirm,
  useToast,
  type Tone,
} from '@cartly/ui';
import { BellRing } from 'lucide-react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { formatRelative, personName } from '../../app/format';
import { useLoader } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { CampaignSections } from '../whatsapp/Campaigns';
import { countBodyParams } from '../whatsapp/Messaging';

interface AccountRow {
  phoneNumbers: {
    id: string;
    displayPhoneNumber: string;
    verifiedName: string | null;
    registeredAt: string | null;
    isOnBizApp: boolean;
  }[];
}
interface TemplateRow {
  name: string;
  language: string;
  status: string;
  components: unknown;
}
interface AlertRow {
  id: string;
  kind: 'back_in_stock' | 'price_drop';
  status: 'active' | 'triggered' | 'sent' | 'skipped' | 'cancelled';
  reason: string | null;
  source: string;
  targetPrice: string | null;
  baselinePrice: string | null;
  triggerPrice: string | null;
  product: string;
  customer: { firstName: string | null; lastName: string | null; phoneE164: string | null };
  createdAt: string;
  notifiedAt: string | null;
}
interface AlertList {
  counts: Record<AlertRow['status'], number>;
  items: AlertRow[];
}

const STATUS_TONE: Record<AlertRow['status'], Tone> = {
  active: 'info',
  triggered: 'warning',
  sent: 'success',
  skipped: 'neutral',
  cancelled: 'neutral',
};
const STATUS_LABEL: Record<AlertRow['status'], string> = {
  active: 'Waiting',
  triggered: 'Sending',
  sent: 'Sent',
  skipped: 'Not sent',
  cancelled: 'Cancelled',
};
const REASON_LABEL: Record<string, string> = {
  no_rule: 'No message set up for this alert type',
  no_phone: 'No phone number',
  expired: 'Too old to send',
  opted_out: 'Customer opted out',
  no_consent: 'No opt-in recorded',
  template_not_approved: 'Template not approved',
  template_not_found: 'Template not found',
  invalid_rule: 'Message setup is invalid',
};

/** Back-in-stock and price-drop alerts: who is waiting, what was sent, and the messages. */
export function AlertsPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const tenantId = session.activeTenant?.id;
  const alerts = useLoader(() => api.get<AlertList>('/product-alerts?limit=100'), tenantId);
  const accounts = useLoader(
    () => api.get<{ accounts: AccountRow[] }>('/whatsapp/accounts').then((r) => r.accounts),
    tenantId,
  );
  const templates = useLoader(
    () => api.get<{ templates: TemplateRow[] }>('/whatsapp/templates').then((r) => r.templates),
    tenantId,
  );

  if (!accounts.data || !templates.data) {
    return accounts.error ? (
      <Alert tone="critical">{accounts.error}</Alert>
    ) : (
      <Spinner size="lg" label="Loading" />
    );
  }
  const phones = accounts.data.flatMap((a) =>
    a.phoneNumbers.map((p) => ({
      id: p.id,
      label: `${p.displayPhoneNumber}${p.verifiedName ? ` · ${p.verifiedName}` : ''}`,
      registeredAt: p.registeredAt,
      coexistence: p.isOnBizApp,
    })),
  );

  const cancel = async (a: AlertRow) => {
    const ok = await confirm({
      title: 'Cancel alert',
      message: `Stop waiting to message ${personName({ ...a.customer, email: null }) || 'this customer'} about ${a.product}?`,
      confirmLabel: 'Cancel alert',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.post(`/product-alerts/${a.id}/cancel`, {});
      void alerts.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const c = alerts.data?.counts;
  return (
    <div className="stack-lg">
      <PageHeader
        title="Stock & price alerts"
        description="Shoppers ask to be told when a sold-out product returns or gets cheaper. Cartly sends one WhatsApp message when it happens."
        actions={
          <div className="text-muted text-sm">
            {c ? `${c.active} waiting · ${c.sent} sent` : null}
          </div>
        }
      />
      {phones.length === 0 && (
        <Alert tone="info" title="Connect WhatsApp first">
          Alerts are sent from a WhatsApp number. <Link to="/whatsapp">Connect an account</Link> and
          wait for a template to be approved.
        </Alert>
      )}
      <Card
        title="Alerts"
        subtitle="Added from your storefront with a publishable key (POST /api/public/v1/storefront/alerts)."
        flush
      >
        {alerts.error && <Alert tone="critical">{alerts.error}</Alert>}
        {!alerts.data || alerts.data.items.length === 0 ? (
          <EmptyState icon={<BellRing size={22} />} title="No alerts yet">
            When a shopper taps &ldquo;Notify me&rdquo; on a sold-out product, it shows up here.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Alert</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Asked</th>
                  <th className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {alerts.data.items.map((a) => (
                  <tr key={a.id}>
                    <td>{a.product}</td>
                    <td>
                      {a.kind === 'back_in_stock'
                        ? 'Back in stock'
                        : `Price drop${a.targetPrice ? ` to ${a.targetPrice}` : ''}`}
                    </td>
                    <td>
                      {personName({ ...a.customer, email: null }) || '—'}
                      <div className="text-muted text-sm">{a.customer.phoneE164 ?? ''}</div>
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                      {a.reason && (
                        <div className="text-muted text-sm">
                          {REASON_LABEL[a.reason] ?? a.reason}
                        </div>
                      )}
                    </td>
                    <td>{formatRelative(a.createdAt)}</td>
                    <td className="table__actions">
                      {can('campaigns:manage') &&
                        (a.status === 'active' || a.status === 'triggered') && (
                          <Button size="sm" variant="secondary" onClick={() => void cancel(a)}>
                            Cancel
                          </Button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {phones.length > 0 && (
        <CampaignSections
          show="alertMessages"
          tenantId={tenantId}
          phones={phones}
          templates={templates.data.map((t) => ({
            name: t.name,
            language: t.language,
            status: t.status,
            bodyParams: countBodyParams(t.components),
          }))}
          canManageChannels={can('channels:manage')}
          canReadCampaigns={can('campaigns:read')}
          canManageCampaigns={can('campaigns:manage')}
        />
      )}
    </div>
  );
}
